import { formatInvalidStoredIconError } from "./format_invalid_stored_icon_error";
import { resolveLucideIconKey } from "./lucide_icon_registry.generated";

/**
 * Canonical stored icon format: Unicode emoji or `lucide:<kebab-case-name>`.
 * One implementation, two modes: writes refuse unknown Lucide names
 * ({@link normalizeStoredIconValue}); rendering stored rows keeps them as they
 * are ({@link normalizeStoredIconValueForDisplay}, used by `@v1/ui`).
 * Pure leaf: no `_generated` or handler imports.
 */

const LUCIDE_PREFIX = "lucide:" as const;
const AGENT_MARK_ICON_VALUE = "haus:agent-mark" as const;
const LEGACY_AGENT_MARK_ICON_VALUE = "lucide:bot" as const;

/** Shared hint for agent tool Zod `.describe()` strings. */
export const STORED_ICON_LUCIDE_FORMAT_HINT: string =
  "emoji oder lucide:kebab-case; veraltete Lucide-Namen werden auto-korrigiert, unbekannte abgelehnt.";

const AGENT_MARK_ICON_VALUES: ReadonlySet<string> = new Set([
  AGENT_MARK_ICON_VALUE,
  LEGACY_AGENT_MARK_ICON_VALUE,
]);

/** What happens to a Lucide name the installed registry does not know. */
type UnknownLucideName = "refuse" | "keep";

/** Convert PascalCase Lucide export names to kebab-case dynamic import keys. */
export function lucidePascalToKebab(name: string): string {
  return name
    .replace(/([a-z0-9])([A-Z])/g, "$1-$2")
    .replace(/([A-Z])([A-Z][a-z])/g, "$1-$2")
    .replace(/([a-zA-Z])(\d)/g, "$1-$2")
    .replace(/(\d)([a-zA-Z])/g, "$1-$2")
    .toLowerCase();
}

function normalizeLucideRawName(raw: string): string {
  const trimmed: string = raw.trim();
  if (trimmed.length === 0) return trimmed;
  if (/^[a-z][a-z0-9]*(-[a-z0-9]+)*$/.test(trimmed)) {
    return trimmed;
  }
  if (/^[A-Z][a-zA-Z0-9]*$/.test(trimmed)) {
    return lucidePascalToKebab(trimmed);
  }
  return trimmed.toLowerCase();
}

function resolveLucideStoredIcon(
  normalized: string,
  originalInput: string,
  unknown: UnknownLucideName,
): string {
  if (!normalized.startsWith(LUCIDE_PREFIX)) return normalized;
  if (AGENT_MARK_ICON_VALUES.has(normalized)) return AGENT_MARK_ICON_VALUE;

  const rawName: string = normalized.slice(LUCIDE_PREFIX.length);
  const resolved: string | null = resolveLucideIconKey(rawName);
  if (resolved !== null) return `${LUCIDE_PREFIX}${resolved}`;
  if (unknown === "keep") return normalized;
  throw new Error(formatInvalidStoredIconError(originalInput, rawName));
}

/** Accepts legacy/agent shapes (PascalCase, bare kebab-case) and normalizes them. */
function normalizeStoredIcon(
  value: string | undefined | null,
  unknown: UnknownLucideName,
): string | undefined {
  if (value == null) return undefined;
  const trimmed: string = value.trim();
  if (trimmed.length === 0) return undefined;

  if (AGENT_MARK_ICON_VALUES.has(trimmed)) return AGENT_MARK_ICON_VALUE;

  let normalized: string;

  if (trimmed.startsWith(LUCIDE_PREFIX)) {
    const raw: string = trimmed.slice(LUCIDE_PREFIX.length);
    const kebab: string = normalizeLucideRawName(raw);
    normalized = `${LUCIDE_PREFIX}${kebab}`;
  } else if (/^[A-Z][a-zA-Z0-9]*$/.test(trimmed)) {
    normalized = `${LUCIDE_PREFIX}${lucidePascalToKebab(trimmed)}`;
  } else if (/^[a-z][a-z0-9]*(-[a-z0-9]+)*$/.test(trimmed)) {
    normalized = `${LUCIDE_PREFIX}${trimmed}`;
  } else {
    return trimmed;
  }

  return resolveLucideStoredIcon(normalized, trimmed, unknown);
}

/** Write side: normalizes, and throws on a Lucide name the registry does not know. */
export function normalizeStoredIconValue(
  value: string | undefined | null,
): string | undefined {
  return normalizeStoredIcon(value, "refuse");
}

/** Read side: same normalization, but an unknown Lucide name is kept, never thrown. */
export function normalizeStoredIconValueForDisplay(
  value: string | undefined | null,
): string | undefined {
  return normalizeStoredIcon(value, "keep");
}

/** Trim; empty clears; otherwise {@link normalizeStoredIconValue}. */
export function normalizeOptionalStoredIconForWrite(
  value: string | undefined | null,
): string | undefined {
  if (value == null) return undefined;
  const trimmed: string = value.trim();
  if (trimmed.length === 0) return undefined;
  return normalizeStoredIconValue(trimmed);
}
